// Shared work-surface for the exception inboxes (Exceptions / Escalations /
// Follow Ups). A compact intake-queue-style table — not cards — because
// these pages are queues someone clears, not dashboards. Inbox semantics: no
// time scope; every open item shows until it's resolved. Row click opens the
// receipt's Ace thread, same as the Intake Queue.

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams, useOutletContext } from 'react-router-dom';
import { useAppShell } from '../contexts/AppShellContext';
import { useAuth } from '../contexts/AuthContext';
import { useTeamMessages } from '../contexts/TeamMessagesContext';
import { getMaterialReceipts, updateMaterialReceipt } from '../services/materialReceiptService';
import type { MaterialReceiptRow } from '../types/api';
import { SearchInput } from './SearchInput';
import { SortableTh } from './SortableTh';
import { useSort, sortRows } from '../utils/useSort';
import { pillStyle, type PillColor } from '../utils/pillStyle';
import { teamLabel } from '../utils/rbac';
import { latestHandoffNote, noteStamp } from '../data/receiptDraft';
import { StatusPill } from './StatusPill';
import { useRowHighlight } from '../utils/useRowHighlight';

const CATEGORY: Record<string, { label: string; cls: string }> = {
  P:  { label: 'Proxy',       cls: 'tp tp-px' },
  CA: { label: 'Corp Action', cls: 'tp tp-ca' },
  R:  { label: 'Regulatory',  cls: 'tp tp-rg' },
  I:  { label: 'Issuer',      cls: 'tp tp-px' },
};

const COLUMNS = ['Received', 'Category', 'Scan #', 'Job #', 'Client Name(s)', 'Status', 'Note', 'Action'];

// Active-pill colours for the per-type sub-filters (mirrors StatusPill palette).
const TYPE_PILL_COLOR: Record<string, PillColor> = {
  'No Job Found':   { bg: '#FEF2F2', fg: '#b91c1c', border: '#FECACA' },
  'Received Short': { bg: '#FEF2F2', fg: '#b91c1c', border: '#FECACA' },
  'On Hold':        { bg: '#FFFBEB', fg: '#92400E', border: '#FCD34D' },
};

// Workflow stage ownership caption (material intake: FC owns, IBS escalation target).
// Matches the static config in docs/rbac.md.
const STAGE_INFO: Record<string, { owner: string; escalateTo: string; escalateToId: 'wayne' | 'cary' }> = {
  exceptions:   { owner: 'Receiving',  escalateTo: 'Proxy/ICT',  escalateToId: 'cary' },
  escalations:  { owner: 'Proxy/ICT',  escalateTo: 'Receiving',  escalateToId: 'wayne' },
  'follow-ups': { owner: 'Receiving',  escalateTo: 'Proxy/ICT',  escalateToId: 'cary' },
  overs:        { owner: 'Receiving',  escalateTo: 'Proxy/ICT',  escalateToId: 'cary' },
};

export interface TypeFilter { id: string; label: string; status: string }

function cellValue(r: MaterialReceiptRow, column: string): string {
  switch (column) {
    case 'Received':        return r.intake_date ?? '';
    case 'Category':        return r.job_category ? (CATEGORY[r.job_category]?.label ?? r.job_category) : '';
    case 'Scan #':          return r.scan_number;
    case 'Job #':           return r.job_numbers.join(', ');
    case 'Client Name(s)':  return r.client_names.join(', ');
    case 'Status':          return r.material_status;
    case 'Note':            return latestHandoffNote(r.note_log)?.text ?? '';
    default:                return '';
  }
}

function fmtDate(iso: string | null): string {
  if (!iso) return '';
  const today = new Date().toISOString().split('T')[0];
  const yest = new Date(Date.now() - 86400000).toISOString().split('T')[0];
  if (iso === today) return 'Today';
  if (iso === yest) return 'Yesterday';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// Inline modal for escalating a row to the stage's target team — the target
// is fixed by STAGE_INFO (no team picker needed), unlike Ace's EscalateModal.
function EscalateRowModal({
  row,
  targetTeamId,
  targetLabel,
  onClose,
  onEscalated,
  showToast,
  authorName,
}: {
  row: MaterialReceiptRow;
  targetTeamId: 'wayne' | 'cary';
  targetLabel: string;
  onClose: () => void;
  onEscalated: (updated: MaterialReceiptRow) => void;
  showToast?: (m: string) => void;
  authorName: string;
}) {
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const { send } = useTeamMessages();

  async function handleSave() {
    setSaving(true);
    try {
      const text = note.trim() || `Escalated to ${targetLabel} for review.`;
      const updated = await updateMaterialReceipt(row.id, {
        escalated_to_team: targetTeamId,
        note_log: [
          ...(row.note_log || []),
          { author: authorName, timestamp: noteStamp(), text, kind: 'note' },
          { author: 'Ace', timestamp: noteStamp(), text: `Escalated to ${targetLabel} — now visible in their Escalations queue and notification panel.`, kind: 'system' },
        ],
      });
      // Both teams work in Ace, so the hand-off is a notification in the
      // other team's bell, not an email.
      send({
        from: targetTeamId === 'cary' ? 'wayne' : 'cary', to: targetTeamId, kind: 'escalation',
        title: `Escalated: Scan ${row.scan_number}`, body: text,
        jobId: row.job_numbers[0], scan: row.scan_number, client: row.client_names.join(', '),
      });
      onEscalated(updated);
      showToast?.(`Escalated to ${targetLabel}`);
      onClose();
    } catch {
      showToast?.('Failed to escalate — try again');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center p-4"
      style={{ background: 'rgba(4,50,67,.5)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl shadow-modal w-[400px] overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-trim">
          <div>
            <div className="text-sm font-semibold text-ink">Escalate to {targetLabel}</div>
            <div className="text-xs text-muted">{row.scan_number} · {row.client_names.join(', ')}</div>
          </div>
          <button onClick={onClose} className="w-7 h-7 rounded-lg flex items-center justify-center text-copy hover:bg-wash">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 2l10 10M12 2L2 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
          </button>
        </div>
        <div className="px-5 py-4">
          <label className="block text-xs font-semibold text-muted uppercase tracking-wide mb-1.5">
            Note (optional)
          </label>
          <textarea
            autoFocus
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="Why are you escalating?"
            rows={3}
            className="w-full px-3 py-2 rounded-lg border border-trim text-sm text-ink outline-none focus:border-teal-400 bg-white resize-none"
          />
          <p className="mt-2 text-xs text-muted">
            {targetLabel} will be notified and this item will show in their Escalations queue.
          </p>
        </div>
        <div className="px-5 pb-5 pt-3 border-t border-trim flex gap-3 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-trim text-sm font-medium text-copy hover:bg-wash">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-[#6d28d9] hover:bg-[#5b21b6] disabled:opacity-50"
          >
            {saving ? 'Escalating…' : `Escalate to ${targetLabel}`}
          </button>
        </div>
      </div>
    </div>
  );
}

export function ExceptionQueueTable({
  title,
  subtitle,
  blurb,
  predicate,
  typeFilters,
  stageKey,
}: {
  title: string;
  subtitle: string;
  blurb: string;
  predicate: (r: MaterialReceiptRow) => boolean;
  typeFilters?: TypeFilter[];
  // Determines escalate-target (STAGE_INFO) and whether Escalate renders at all.
  stageKey?: string;
}) {
  const { setTopbar } = useAppShell();
  const { can, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows]           = useState<MaterialReceiptRow[]>([]);
  const [search, setSearch]       = useState('');
  const { sort, toggle }          = useSort();
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);
  const [escalateRow, setEscalateRow] = useState<MaterialReceiptRow | null>(null);
  const { rowRef, highlightClass } = useRowHighlight();

  const rawType = searchParams.get('type');
  const typeFilter = typeFilters?.find(t => t.id === rawType) ?? null;
  const setTypeFilter = (id: string | null) =>
    setSearchParams(id ? { type: id } : {}, { replace: true });

  const { showToast } = (useOutletContext() as any) || {};

  useEffect(() => { setTopbar(title, subtitle); }, [setTopbar, title, subtitle]);

  useEffect(() => {
    let alive = true;
    const load = () => getMaterialReceipts()
      .then(r => { if (alive) setRows(r); })
      .catch(e => { if (alive) setError(e.message); })
      .finally(() => { if (alive) setLoading(false); });
    load();
    const iv = setInterval(load, 3000);
    return () => { alive = false; clearInterval(iv); };
  }, []);

  const items = useMemo(() => rows.filter(predicate), [rows, predicate]);

  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const t of typeFilters ?? []) counts[t.id] = items.filter(r => r.material_status === t.status).length;
    return counts;
  }, [items, typeFilters]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    const data = items.filter(r => {
      if (typeFilter && r.material_status !== typeFilter.status) return false;
      if (q && !`${r.client_names.join(' ')} ${r.scan_number}`.toLowerCase().includes(q)) return false;
      return true;
    });
    return sortRows(data, sort, cellValue);
  }, [items, typeFilter, search, sort]);

  const openAce = (r: MaterialReceiptRow) =>
    navigate('/ace', { state: { receiptThread: { receiptId: r.id, scan: r.scan_number, client: r.client_names.join(', '), row: r } } });

  const onResolved = (updated: MaterialReceiptRow) =>
    setRows(prev => prev.map(r => r.id === updated.id ? updated : r));

  const stage = stageKey ? STAGE_INFO[stageKey] : null;

  if (error) return <div className="px-6 py-5 text-sm text-red-600">{error}</div>;

  // Can this row still be escalated onward? The Escalations queue never
  // offers Escalate at all.
  const canEscalateRow = (r: MaterialReceiptRow) =>
    !!stage && stageKey !== 'escalations' && r.escalated_to_team !== stage.escalateToId;

  const renderRow = (r: MaterialReceiptRow) => {
    const handoff = latestHandoffNote(r.note_log);

    return (
      <tr key={r.id} ref={rowRef(r.id)} data-tour={`exception-row-${r.scan_number}`} className={`group hover:bg-wash transition-colors cursor-pointer ${highlightClass(r.id)}`} onClick={() => openAce(r)}>
        <td className="px-2 py-1.5 border-b border-wash text-xs text-muted whitespace-nowrap align-top">
          {fmtDate(r.intake_date)}{r.intake_time ? <span className="text-muted"> · {r.intake_time}</span> : ''}
        </td>
        <td className="px-2 py-1.5 border-b border-wash whitespace-nowrap align-top">
          {r.job_category && CATEGORY[r.job_category]
            ? <span className={CATEGORY[r.job_category].cls}>{CATEGORY[r.job_category].label}</span>
            : <span className="text-xs text-muted">—</span>}
        </td>
        <td className="px-2 py-1.5 border-b border-wash text-xs font-medium text-ink whitespace-nowrap align-top group-hover:text-teal-600 transition-colors">{r.scan_number}</td>
        <td className="px-2 py-1.5 border-b border-wash text-xs text-copy whitespace-nowrap align-top">{r.job_numbers.length ? r.job_numbers.join(', ') : <span className="text-muted">—</span>}</td>
        <td className="px-2 py-1.5 border-b border-wash align-top">
          <div className="max-w-[240px] text-xs text-copy break-words" title={r.client_names.join(', ')}>{r.client_names.join(', ')}</div>
        </td>
        <td className="px-2 py-1.5 border-b border-wash whitespace-nowrap align-top">
          <span className="inline-flex items-center gap-1.5">
            <StatusPill status={r.material_status} />
            {r.escalated_to_team && (
              <span className="px-1.5 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap" style={{ background: '#EDE9FE', color: '#6d28d9' }} title={`Escalated to ${teamLabel(r.escalated_to_team)}`}>
                → {teamLabel(r.escalated_to_team)}
              </span>
            )}
            {r.overs_status && (
              <span
                className="px-1.5 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap"
                style={r.overs_status === 'Pending'
                  ? { background: '#FEF3C7', color: '#92400E' }
                  : { background: '#DCFCE7', color: '#166534' }}
                title={`Overs ${r.overs_status}`}
              >
                Overs · {r.overs_status}
              </span>
            )}
          </span>
        </td>
        <td className="px-2 py-1.5 border-b border-wash align-top">
          <div className="max-w-[240px] text-xs text-copy break-words" title={handoff ? `${handoff.author} · ${handoff.timestamp}` : undefined}>
            {handoff?.text || <span className="text-muted">—</span>}
          </div>
        </td>
        {/* ── Action chips ───────────────────────────────── */}
        <td className="px-2 py-1.5 border-b border-wash whitespace-nowrap align-top" onClick={e => e.stopPropagation()}>
          <div className="flex items-center gap-1.5 flex-wrap">
            {!r.escalated_to_team && (
              can('receipt.match') ? (
                <button
                  onClick={e => { e.stopPropagation(); openAce(r); }}
                  className="px-2.5 py-1 rounded-full text-xs font-semibold transition-colors hover:opacity-80"
                  style={{ background: '#FEF2F2', color: '#b91c1c', border: '1px solid #FECACA' }}
                  title="Open in Ace to review and resolve"
                >
                  Resolve
                </button>
              ) : (
                <span
                  className="px-2.5 py-1 rounded-full text-xs font-semibold opacity-40 cursor-not-allowed"
                  style={{ background: '#FEF2F2', color: '#b91c1c', border: '1px solid #FECACA' }}
                  title="Requires receipt.match — held by Team Operator or Manager"
                >
                  Resolve
                </span>
              )
            )}
            {canEscalateRow(r) && (
              can('receipt.escalate') ? (
                <button
                  onClick={e => { e.stopPropagation(); setEscalateRow(r); }}
                  className="px-2.5 py-1 rounded-full text-xs font-semibold transition-colors hover:opacity-80"
                  style={{ background: '#EDE9FE', color: '#6d28d9', border: '1px solid #C4B5FD' }}
                  title={`Escalate to ${stage!.escalateTo}`}
                >
                  Escalate
                </button>
              ) : (
                <span
                  className="px-2.5 py-1 rounded-full text-xs font-semibold opacity-40 cursor-not-allowed"
                  style={{ background: '#EDE9FE', color: '#6d28d9', border: '1px solid #C4B5FD' }}
                  title="Requires receipt.escalate — held by Team Operator or Manager"
                >
                  Escalate
                </span>
              )
            )}
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex items-center gap-3 px-6 pt-4 pb-4 border-b border-trim flex-shrink-0 flex-wrap gap-y-2">
        <div className="px-2.5 py-0.5 rounded-full text-xs font-semibold" style={{ background: '#F5F7FA', color: '#595A60' }}>{filtered.length} items</div>
        {typeFilters && (
          <div className="flex gap-1.5 flex-wrap">
            <button onClick={() => setTypeFilter(null)} className="px-3 py-1 rounded-full text-xs font-medium transition-colors" style={pillStyle(!typeFilter)}>All</button>
            {typeFilters.map(t => (
              <button key={t.id} onClick={() => setTypeFilter(typeFilter?.id === t.id ? null : t.id)}
                className="px-3 py-1 rounded-full text-xs font-medium transition-colors inline-flex items-center gap-1.5"
                style={pillStyle(typeFilter?.id === t.id, TYPE_PILL_COLOR[t.status])}>
                {t.label}
                {(typeCounts[t.id] ?? 0) > 0 && <span className="tabular-nums font-semibold opacity-80">{typeCounts[t.id]}</span>}
              </button>
            ))}
          </div>
        )}
        <span className="text-xs text-muted hidden xl:inline">{blurb}</span>
        <div className="ml-auto">
          <SearchInput value={search} onChange={setSearch} placeholder="Search scans, clients…" width={220} />
        </div>
      </div>

      <div className="flex-1 overflow-auto px-6 pb-6">
        {loading && <div className="text-sm text-muted py-4">Loading…</div>}
        {!loading && filtered.length === 0 ? (
          <div className="max-w-2xl mt-6 px-4 py-6 rounded-xl border border-trim bg-wash text-center">
            <div className="text-sm font-medium text-[#0F6E56] mb-0.5">All clear</div>
            <div className="text-xs text-muted">No {title.toLowerCase()} items right now.</div>
          </div>
        ) : (
          <table data-tour="fulfillment-exceptions" className="text-xs" style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%' }}>
            <thead>
              <tr>
                {COLUMNS.map(h => <SortableTh key={h} label={h} column={h} sort={sort} onSort={h === 'Action' ? () => {} : toggle} />)}
              </tr>
            </thead>
            <tbody>
              {filtered.map(renderRow)}
            </tbody>
          </table>
        )}
      </div>

      {escalateRow && stage && (
        <EscalateRowModal
          row={escalateRow}
          targetTeamId={stage.escalateToId}
          targetLabel={stage.escalateTo}
          onClose={() => setEscalateRow(null)}
          onEscalated={updated => { onResolved(updated); setEscalateRow(null); }}
          showToast={showToast}
          authorName={user?.name?.split(' ')[0] || 'Operator'}
        />
      )}
    </div>
  );
}
